import ui from '@nuxt/ui/vue-plugin'
import { AxiosError } from 'axios'
import { createApp } from 'vue'

import type { StartupScreenState } from './bootstrap/startupScreen'

import App from './App.vue'
import { initErrorReporting } from './bootstrap/errorReporting'
import { reloadForStaleAssets } from './bootstrap/staleAssets'
import {
  hideStartupScreen,
  setStartupRetryHandler,
  setStartupScreenState,
} from './bootstrap/startupScreen'
import 'inter-ui/inter-display.css'
import 'inter-ui/inter-variable.css'

import { useLayout } from './composables/useLayout'
import { useUser } from './composables/useUser'
import { appTooltip } from './directives/appTooltip'
import i18n from './i18n'
import router from './router'
import {
  applySystemEnvironmentBodyClass,
  getSystemEnvironmentType,
} from './utils/systemEnvironment'
import './assets/styles/main.css'

const { initTheme } = useLayout()

const app = createApp(App)

const { clearUserInfo, fetchUserInfo } = useUser()
initTheme()
applySystemEnvironmentBodyClass(getSystemEnvironmentType())
const STARTUP_RETRY_DELAY_MS = 8000

let hasMounted = false
let isBooting = false
let retryTimeoutId: number | null = null
let retryMode: 'boot' | 'reload' = 'boot'

// Not `preventDefault()`ed: that would make the failed import resolve to `undefined`, which
// vue-router reports as "Couldn't resolve component". Letting it reject keeps the real error
// (the router reloads to the page being opened, see router/index.ts) and it is on the
// error-reporting ignore list. The deferred call covers imports that are not route components;
// when the router already started a reload, this one sees the guard and does nothing.
window.addEventListener('vite:preloadError', (event) => {
  setTimeout(() => {
    if (!reloadForStaleAssets()) {
      console.error('A stale frontend asset was requested after reload.', event.payload)
    }
  }, 0)
})

const mountApp = async () => {
  // Before the plugins below and before mount, so a failure in any of them is itself reported.
  initErrorReporting(app, router)

  app.use(i18n.i18n)
  app.use(router)
  app.use(ui)
  // Register the router injector plugin
  import('./plugins/routerInjector')
    .then((routerInjector) => {
      app.use(routerInjector.default, { router })
    })
    .catch((error: Error) => {
      console.error('Error loading router injector plugin:', error)
    })

  app.directive('app-tooltip', appTooltip)
  app.mount('#app')
  hideStartupScreen()
}

const clearRetryTimeout = () => {
  if (retryTimeoutId === null) {
    return
  }

  window.clearTimeout(retryTimeoutId)
  retryTimeoutId = null
}

const scheduleAutoRetry = () => {
  if (hasMounted || retryTimeoutId !== null) {
    return
  }

  retryTimeoutId = window.setTimeout(() => {
    retryTimeoutId = null
    void bootAttempt(false)
  }, STARTUP_RETRY_DELAY_MS)
}

const isRecoverableUserInfoAuthError = (error: unknown) =>
  error instanceof AxiosError &&
  error.response !== undefined &&
  [401, 403, 404].includes(error.response.status)

const getStartupFailureState = (error: unknown): StartupScreenState => {
  if (error instanceof AxiosError) {
    if (!error.response && navigator.onLine === false) {
      return 'offline'
    }

    return 'unavailable'
  }

  return 'startupError'
}

const bootAttempt = async (showLoadingState = true) => {
  if (hasMounted || isBooting) {
    return
  }

  isBooting = true
  retryMode = 'boot'
  if (showLoadingState) {
    setStartupScreenState('loading')
  }

  try {
    // Keep auth state ready before the router starts navigation.
    await fetchUserInfo(false, true)
  } catch (error) {
    if (isRecoverableUserInfoAuthError(error)) {
      console.warn(
        'User info request returned an auth status during startup; continuing anonymously.',
        error,
      )
      clearUserInfo()
    } else {
      console.error('Error fetching user info during startup:', error)
      setStartupScreenState(getStartupFailureState(error))
      scheduleAutoRetry()
      isBooting = false
      return
    }
  }

  try {
    await mountApp()
    hasMounted = true
    clearRetryTimeout()
  } catch (error) {
    console.error('Error mounting application:', error)
    retryMode = 'reload'
    setStartupScreenState('startupError')
  } finally {
    isBooting = false
  }
}

const boot = async () => {
  setStartupRetryHandler(() => {
    if (retryMode === 'reload') {
      window.location.reload()
      return
    }

    clearRetryTimeout()
    void bootAttempt(true)
  })

  await bootAttempt(true)
}

void boot()
